import {
  Component, EventEmitter, Input, OnDestroy, OnInit, Output,
  ChangeDetectionStrategy, ChangeDetectorRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subscription, interval } from 'rxjs';
import { take } from 'rxjs/operators';
import { NmiPosPaymentService } from '../../services/nmi-pos-payment.service';
import {
  AsyncStatusResponse, PosConfirmRequest, PosDevice, PosSaleResponse,
} from '../../../models/nmi.models';

@Component({
  selector: 'nmi-pos-payment',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './nmi-pos-payment.component.html',
  styleUrls: ['./nmi-pos-payment.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NmiPosPaymentComponent implements OnInit, OnDestroy {
  @Input() amount?: number;
  @Input() customerId?: string;
  @Input() addedBy?: string;

  @Output() paymentComplete = new EventEmitter<PosSaleResponse>();
  @Output() paymentCancelled = new EventEmitter<void>();

  currentStep: 1 | 2 | 3 | 4 = 1;

  // Step 1
  devices: PosDevice[] = [];
  loadingDevices = false;
  selectedDeviceId: string | null = null;

  // Step 2
  saleAmount = 0;
  saleType: 'sale' | 'auth' = 'sale';

  // Step 3
  elapsedSeconds = 0;
  private elapsedTimer: Subscription | null = null;
  private pollSub: Subscription | null = null;

  // Step 4
  saleResult: PosSaleResponse | null = null;
  finalStatus: AsyncStatusResponse | null = null;
  paymentSucceeded = false;
  private paymentIntentId: number | null = null;
  private paymentEventEmitted = false;

  private subs = new Subscription();

  constructor(
    private posService: NmiPosPaymentService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    if (this.amount != null && this.amount > 0) this.saleAmount = this.amount;
    this.loadDevices();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
    this.stopTimers();
  }

  loadDevices(): void {
    this.loadingDevices = true;
    this.posService.getDevices().pipe(take(1)).subscribe({
      next: devices => {
        this.devices = devices;
        if (devices.length > 0) this.selectedDeviceId = devices[0].poi_device_id;
        this.loadingDevices = false;
        this.cdr.markForCheck();
      },
      error: () => {
        this.loadingDevices = false;
        this.cdr.markForCheck();
      },
    });
  }

  goToStep2(): void {
    if (!this.selectedDeviceId) return;
    this.currentStep = 2;
    this.cdr.markForCheck();
  }

  backToStep1(): void { this.currentStep = 1; this.cdr.markForCheck(); }

  startPayment(): void {
    if (!this.saleAmount || this.saleAmount <= 0) return;
    this.currentStep = 3;
    this.cdr.markForCheck();
    this.submitSale();
  }

  private submitSale(): void {
    this.elapsedSeconds = 0;
    this.elapsedTimer = interval(1000).subscribe(() => {
      this.elapsedSeconds++;
      this.cdr.markForCheck();
    });
    this.subs.add(this.elapsedTimer);

    this.posService.processSale({
      poi_device_id: this.selectedDeviceId!,
      amount: this.saleAmount,
      type: this.saleType,
      customer_id: this.customerId,
    }).pipe(take(1)).subscribe({
      next: response => {
        this.saleResult = response;
        this.paymentIntentId = response.payment_intent_id ?? null;
        if (!response.success || !response.async_status_guid) {
          this.stopTimers();
          this.paymentSucceeded = false;
          this.currentStep = 4;
          this.cdr.markForCheck();
          return;
        }
        this.startPolling(response.async_status_guid);
      },
      error: () => {
        this.stopTimers();
        this.saleResult = { success: false, response_text: 'Failed to reach terminal.' };
        this.paymentSucceeded = false;
        this.currentStep = 4;
        this.cdr.markForCheck();
      },
    });
  }

  private startPolling(asyncGuid: string): void {
    this.pollSub = this.posService.pollUntilComplete(asyncGuid).subscribe({
      next: status => {
        this.stopTimers();
        this.finalStatus = status;
        this.paymentSucceeded =
          status.success === true ||
          (status.async_status === 'interactionComplete' && status.success !== false);

        if (this.paymentSucceeded && this.paymentIntentId != null && status.transaction_id) {
          const req: PosConfirmRequest = {
            payment_intent_id: this.paymentIntentId,
            transaction_id: status.transaction_id,
            condition: status.condition ?? '',
            auth_code: status.auth_code ?? '',
            amount: this.saleAmount,
            added_by: this.addedBy,
          };
          this.posService.confirmPayment(req).pipe(take(1)).subscribe({
            next: () => {
              this.currentStep = 4;
              this.cdr.markForCheck();
              this.emitPaymentComplete();
            },
            error: (err) => {
              console.error('POS confirm failed (payment still processed on terminal):', err);
              this.currentStep = 4;
              this.cdr.markForCheck();
              this.emitPaymentComplete();
            },
          });
        } else {
          this.currentStep = 4;
          this.cdr.markForCheck();
          if (this.paymentSucceeded) this.emitPaymentComplete();
        }
      },
      error: () => {
        this.stopTimers();
        this.finalStatus = { async_status: 'error', response_text: 'Terminal timed out.' };
        this.paymentSucceeded = false;
        this.currentStep = 4;
        this.cdr.markForCheck();
      },
    });
    this.subs.add(this.pollSub);
  }

  private stopTimers(): void {
    this.elapsedTimer?.unsubscribe();
    this.pollSub?.unsubscribe();
    this.elapsedTimer = null;
    this.pollSub = null;
  }

  get displayTransactionId(): string | undefined {
    return this.finalStatus?.transaction_id ?? this.saleResult?.transaction_id;
  }
  get displayAuthCode(): string | undefined { return this.finalStatus?.auth_code; }
  get displayResponseText(): string | undefined {
    return this.finalStatus?.response_text ?? this.saleResult?.response_text;
  }

  done(): void {
    this.emitPaymentComplete();
  }

  tryAgain(): void {
    this.stopTimers();
    this.saleResult = null;
    this.finalStatus = null;
    this.paymentSucceeded = false;
    this.paymentIntentId = null;
    this.paymentEventEmitted = false;
    this.elapsedSeconds = 0;
    this.currentStep = 1;
    this.loadDevices();
    this.cdr.markForCheck();
  }

  private emitPaymentComplete(): void {
    if (!this.paymentEventEmitted) {
      this.paymentEventEmitted = true;
      this.paymentComplete.emit(this.saleResult ?? { success: this.paymentSucceeded });
    }
  }

  trackByDeviceId(_i: number, d: PosDevice): string { return d.poi_device_id; }
}
